import React from 'react';
import moment from 'moment';
import { Image } from 'antd';
// Helpers
import { isImage } from 'helpers/isImage';
import {
  formatFollowerCount,
  getFollowerBoundLabel,
  getPlatformCriteria,
  hasFollowerBounds,
  hasPlatformFollowerBounds,
  hasRootFollowerBounds,
  isCustomLikeCampaign,
  usesExplicitFollowerBounds,
} from 'helpers/campaign';
import {
  formatLocationString,
  getCustomActivity,
  getInPersonAppearanceActivity,
  getSocialActivity,
  getVirtualAppearanceActivity,
} from 'helpers/cbActivity';
import { platformLabel } from 'helpers/customCampaignConstants';
// Types
import { CampaignSmPlatform, CampaignType } from 'api/graphql-global-types';
import {
  GetCampaignsV2_getCampaigns_entities,
  GetCampaignsV2_getCampaigns_entities_agency,
  GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_files,
  GetCampaignsV2_getCampaigns_entities_search,
} from 'api/campaignV2/types/GetCampaignsV2';
// Constants
import { FALLBACK_IMAGE } from 'constants/global';
// Icons
import { DownloadOutlined } from '@ant-design/icons';
// Ui
import { DetailCard, DetailGrid, DetailItem } from 'ui/DetailList/DetailList';
// Style
import styles from './CampaignRecordData.module.scss';

type CampaignRecordDataProps = {
  record: GetCampaignsV2_getCampaigns_entities;
};

const CampaignRecordData = ({
  record,
}: CampaignRecordDataProps): JSX.Element | null => {
  const socialActivity = getSocialActivity(record);
  const virtualAppearanceActivity = getVirtualAppearanceActivity(record);
  const inPersonAppearanceActivity = getInPersonAppearanceActivity(record);
  const customActivity = getCustomActivity(record);
  const isCustomLike = isCustomLikeCampaign(record?.type);
  const customSearch =
    record?.search?.__typename === 'CustomSearchCriteria'
      ? record.search
      : null;

  const files =
    socialActivity?.files?.length > 0
      ? socialActivity.files
      : virtualAppearanceActivity?.files?.length > 0
      ? virtualAppearanceActivity.files
      : inPersonAppearanceActivity?.files?.length > 0
      ? inPersonAppearanceActivity.files
      : customActivity?.files?.length > 0
      ? customActivity.files
      : [];

  const brand = record?.agency as GetCampaignsV2_getCampaigns_entities_agency;
  const search = record?.search as GetCampaignsV2_getCampaigns_entities_search;
  const locations = search?.locations;
  const platformCriteria = getPlatformCriteria(search);
  const platformHasFollowerBounds = hasPlatformFollowerBounds(platformCriteria);
  const showRootFollowerBounds = hasRootFollowerBounds(
    search,
    record?.type,
    platformHasFollowerBounds
  );
  const showLegacyFollowing =
    !usesExplicitFollowerBounds(record?.type) &&
    !!(search?.minFollowers || search?.maxFollowers);

  const usageRightsMonths = record?.usageRightsDuration
    ? (record.usageRightsDuration.years ?? 0) * 12 +
      (record.usageRightsDuration.months ?? 0)
    : 0;

  const questions = record?.questions?.length
    ? [...record.questions].sort((a, b) => a.order - b.order)
    : [];

  return (
    <div className={styles.root}>
      <DetailCard title="Campaign">
        <DetailGrid>
          <DetailItem label="ID" value={record?.id} mono />
          <DetailItem label="External name" value={record?.name} />
          <DetailItem label="Internal name" value={record?.internalName} />
          {isCustomLike && (
            <DetailItem
              label="Visibility"
              value={record?.isPrivate ? 'Private' : 'Public'}
            />
          )}
          {record?.search?.smPlatforms?.[0] && (
            <DetailItem
              label="Platform"
              value={
                record.search.smPlatforms[0] === CampaignSmPlatform.TW
                  ? 'X'
                  : record.search.smPlatforms[0]
              }
            />
          )}
          <DetailItem
            label="Submitted"
            value={moment(record?.createdAt).format('MM/DD/YYYY')}
          />
          {record?.applicationDeadlineDays ? (
            <DetailItem
              label="Application deadline"
              value={
                record?.applicationDeadlineEnabled
                  ? `${record.applicationDeadlineDays} days`
                  : 'Off'
              }
            />
          ) : null}
          {record?.applicationDeadlineEnabled && (
            <DetailItem
              label="Applications close"
              value={moment(record?.dueDate).format('MM/DD/YYYY')}
            />
          )}
        </DetailGrid>
      </DetailCard>

      <DetailCard title="Brand contact">
        <DetailGrid>
          <DetailItem
            label="Email"
            value={
              brand?.email ? (
                <a href={`mailto:${brand.email}`}>{brand.email}</a>
              ) : null
            }
          />
          <DetailItem label="Phone" value={brand?.phoneNumber} />
        </DetailGrid>
      </DetailCard>

      <DetailCard title="Budget">
        <DetailGrid>
          {!isCustomLike && (
            <DetailItem
              label="Max budget set by brand"
              value={record?.estimatedBudget}
            />
          )}
          <DetailItem
            label="Total budget"
            value={record?.budget}
            hint={
              record?.type === CampaignType.DirectBooking
                ? 'Counts money already captured. The launch payment is a hold on the offer amount and becomes budget once the talent accepts, so this stays 0 until then and does not follow "Cost per athlete".'
                : undefined
            }
            wide={record?.type === CampaignType.DirectBooking}
          />
          <DetailItem label="Remaining" value={record?.budgetLeft} />
          <DetailItem
            label="Cost per athlete"
            value={record?.flatFeePerStore}
          />
        </DetailGrid>
      </DetailCard>

      <DetailCard title="Usage rights">
        <DetailGrid>
          <DetailItem
            label="Granted"
            value={record?.usageRights ? 'Yes' : 'No'}
          />
          {record?.type !== CampaignType.DirectBooking && (
            <DetailItem
              label="Terms"
              value={record?.usageRightsText}
              emptyText="None given"
              wide
            />
          )}
          {isCustomLike && usageRightsMonths > 0 && (
            <DetailItem
              label="Duration"
              value={`${usageRightsMonths} month(s)`}
            />
          )}
          {isCustomLike && !!record?.usageRightsScopes?.length && (
            <DetailItem
              label="Scopes"
              value={record.usageRightsScopes.join(', ')}
              wide
            />
          )}
        </DetailGrid>
      </DetailCard>

      <DetailCard title="Talent criteria">
        <DetailGrid>
          {!!search?.sports?.length && (
            <DetailItem
              label={
                search.sports.length > 5
                  ? `Sports (${search.sports.length} selected)`
                  : 'Sports'
              }
              value={search.sports.join(', ')}
              wide
            />
          )}
          {!!search?.genders && (
            <DetailItem label="Gender" value={search.genders} />
          )}
          {!!search?.athleteMinAge && (
            <DetailItem label="Minimum age" value={search.athleteMinAge} />
          )}
          {!!search?.athleteMaxAge && (
            <DetailItem label="Maximum age" value={search.athleteMaxAge} />
          )}

          {platformHasFollowerBounds &&
            platformCriteria.map((pc) => {
              if (!hasFollowerBounds(pc.minFollowers, pc.maxFollowers)) {
                return null;
              }

              return (
                <React.Fragment key={pc.id || pc.platform}>
                  <DetailItem
                    label={getFollowerBoundLabel('minimum', pc.platform)}
                    value={formatFollowerCount(pc.minFollowers)}
                  />
                  <DetailItem
                    label={getFollowerBoundLabel('maximum', pc.platform)}
                    value={formatFollowerCount(pc.maxFollowers)}
                  />
                </React.Fragment>
              );
            })}

          {showRootFollowerBounds && (
            <>
              <DetailItem
                label={getFollowerBoundLabel('minimum')}
                value={formatFollowerCount(search.minFollowers)}
              />
              <DetailItem
                label={getFollowerBoundLabel('maximum')}
                value={formatFollowerCount(search.maxFollowers)}
              />
            </>
          )}

          {showLegacyFollowing && (
            <DetailItem
              label="Social media following"
              value={search.minFollowers || search.maxFollowers}
            />
          )}

          {!!platformCriteria.length && (
            <DetailItem
              label="Platform criteria"
              value={platformCriteria.map((pc) => (
                <div key={pc.id || pc.platform}>
                  {platformLabel(pc.platform)}
                  {pc.minEngagementRate != null &&
                    ` · ${pc.minEngagementRate}+ engagement`}
                </div>
              ))}
              wide
            />
          )}

          {!!locations?.length && (
            <DetailItem
              label="Locations"
              value={locations.map((location) => (
                <div key={location.placeId}>
                  {formatLocationString(location)}
                </div>
              ))}
              wide
            />
          )}

          {isCustomLike &&
            (customSearch?.excludeWorkedWith ||
              customSearch?.excludeDeclined) && (
              <DetailItem
                label="Excluded talent"
                value={[
                  customSearch?.excludeWorkedWith && 'Previously worked with',
                  customSearch?.excludeDeclined && 'Previously declined',
                ]
                  .filter(Boolean)
                  .join(', ')}
                wide
              />
            )}
        </DetailGrid>
      </DetailCard>

      {questions.length > 0 && (
        <DetailCard title="Application questions">
          <ol className={styles.questions}>
            {questions.map((item) => (
              <li key={item.id} className={styles.question}>
                <span className={styles.questionText}>{item.question}</span>
                <span className={styles.questionType}>{item.type}</span>
              </li>
            ))}
          </ol>
        </DetailCard>
      )}

      <DetailCard title="Examples">
        {files?.length > 0 ? (
          <div className={styles.examples}>
            {files.map(
              (
                item: GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_files
              ) => {
                const fileExtension = item?.key?.split('.')[1] || '';

                return isImage(fileExtension) ? (
                  <Image
                    fallback={FALLBACK_IMAGE}
                    key={item.key}
                    width={160}
                    height={160}
                    src={item.url || ''}
                    style={{ objectFit: 'cover', borderRadius: 8 }}
                    alt={item.key || 'uploads'}
                  />
                ) : (
                  <a
                    key={item.key}
                    className={styles.exampleFile}
                    href={item.url}
                    download={item.key}
                    title={item.key}
                  >
                    <DownloadOutlined /> {item.key || 'Download file'}
                  </a>
                );
              }
            )}
          </div>
        ) : (
          <p className={styles.noExamples}>
            The brand did not attach any reference material.
          </p>
        )}
      </DetailCard>
    </div>
  );
};

export default CampaignRecordData;
